import { useDialogs } from "@toolpad/core/useDialogs";
import { useEffect } from "react";
import { useBlocker } from "react-router";

/**
 * Blocks navigation and shows a confirmation dialog when the user attempts to leave the page.
 *
 * @param shouldBlock - Whether to block navigation (e.g. when there are unsaved changes).
 * @param message - The confirmation message shown to the user. Defaults to:
 *   `"You have unsaved changes. Do you want to leave without saving?"`
 *
 * @example
 * const [isDirty, setIsDirty] = useState(false);
 * useNavigationWarning(isDirty);
 */
export function useNavigationWarning(
    shouldBlock: boolean,
    message: string = 'You have unsaved changes. Do you want to leave without saving?'
) {
    const blocker = useBlocker(shouldBlock);
    const dialogs = useDialogs();

    useEffect(() => {
        // when the blocker becomes blocked
        if (blocker.state === 'blocked') {
            // confirm the user wants to discard changes
            dialogs.confirm(message, {
                title: 'Save Changes?',
                cancelText: 'Stay Here',
                okText: 'Leave',
                severity: 'error'
            }
            ).then((confirmed) => {
                // if the user confirmed, proceed
                if (confirmed) {
                    blocker.proceed();
                    return;
                }

                // otherwise reset the blocker
                blocker.reset();
            })
        }
    }, [blocker, dialogs, message]);
}

export default useNavigationWarning;